import { defineStore } from 'pinia';
import { computed, ref, toValue } from 'vue';
import api from '@/app/api/axios.js';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import router from '@/app/router/index.js';
import { findIncludedResource, resolveRelationship } from '@/shared/utils/jsonapi.js';
import { DomainStatus } from '#modules/domains/models/DomainStatus.js';

export const useDomainStore = defineStore('domain', () => {
    const loading = ref(true);

    const domain = ref(null);

    const registrantIdentity = ref();
    const billingIdentity = ref();
    const adminIdentity = ref();
    const techIdentity = ref();

    const tldRequirements = ref();
    const nameservers = ref({});
    const subscription = ref(null);

    const status = computed(() => {
        if (!domain.value) {
            return null;
        }

        return domain.value.attributes.status;
    });

    const canEditContacts = computed(() => {
        if (!domain.value) {
            return false;
        }

        return [DomainStatus.Active, DomainStatus.RegistrationFailed, DomainStatus.TransferFailed].includes(
            domain.value.attributes.status
        );
    });

    const canLock = computed(() => {
        if (!domain.value || !tldRequirements.value) {
            return false;
        }

        return tldRequirements.value.attributes?.lock ?? false;
    });

    const isLocked = computed(() => {
        if (!domain.value) {
            return false;
        }

        return domain.value.attributes?.locked ?? false;
    });

    const isActive = computed(() => {
        return hasStatus(DomainStatus.Active);
    });

    const isQuarantined = computed(() => {
        return hasStatus(DomainStatus.InQuarantine);
    });

    const isCancelled = computed(() => {
        return hasStatus(DomainStatus.Cancelled);
    });

    const registrationFailed = computed(() => {
        return hasStatus(DomainStatus.RegistrationFailed);
    });

    const transferFailed = computed(() => {
        return hasStatus(DomainStatus.TransferFailed);
    });

    const isExpired = computed(() => {
        return hasStatus(DomainStatus.Expired);
    });

    const fetchDomain = async (_id) => {
        const id = toValue(_id);
        const isDifferentDomain = domain.value?.id !== id;

        if (isDifferentDomain) {
            domain.value = null;
            loading.value = true;
            registrantIdentity.value = undefined;
            billingIdentity.value = undefined;
            adminIdentity.value = undefined;
            techIdentity.value = undefined;
            tldRequirements.value = undefined;
            nameservers.value = {};
            subscription.value = null;
        }

        let responseData = null;

        try {
            const response = await api.get(`/general/domains/${id}`);

            domain.value = response.data.data;

            const registrantContactId = response.data.data.relationships.registrantContact?.data?.id;

            if (!registrantContactId) {
                throw new Error('Domain missing registrantContact relationship');
            }

            const registrant = await fetchContact(registrantContactId);
            const [billing, admin, tech] = await Promise.all([
                fetchContact(
                    response.data.data.relationships.billingContact?.data?.id ?? registrantContactId,
                    registrant
                ),
                fetchContact(
                    response.data.data.relationships.adminContact?.data?.id ?? registrantContactId,
                    registrant
                ),
                fetchContact(response.data.data.relationships.techContact?.data?.id ?? registrantContactId, registrant),
            ]);

            registrantIdentity.value = registrant;
            billingIdentity.value = billing;
            adminIdentity.value = admin;
            techIdentity.value = tech;

            tldRequirements.value = findIncludedResource(
                response.data.included,
                'tldRequirement',
                response.data.data.relationships.tldRequirement?.data?.id
            );
            nameservers.value =
                findIncludedResource(
                    response.data.included,
                    'nameserverGroup',
                    response.data.data.relationships.nameserverGroup?.data?.id
                )?.attributes ?? {};
            subscription.value = findIncludedResource(
                response.data.included,
                'subscription',
                response.data.data.relationships.subscription?.data?.id
            );

            responseData = response.data;
        } catch (e) {
            if (e.response?.status === 500 && e.response?.data?.errors?.[0]?.title === 'NotFoundHttpException') {
                await router.push({ name: 'dashboard' });
                return;
            }

            unexpectedError(e);
        } finally {
            loading.value = false;
        }

        return responseData;
    };

    const refetchDomain = async () => {
        if (!domain.value) {
            return;
        }

        const id = domain.value.id;
        domain.value = null;
        loading.value = true;

        await fetchDomain(id);
    };

    const fetchContact = async (contactId, defaultContact = null) => {
        if (!contactId) {
            return null;
        }

        if (contactId === defaultContact?.contact?.id) {
            return { ...defaultContact };
        }

        try {
            const response = await api.get(`/general/contacts/${contactId}`);

            return {
                ...response.data.data,
                contact: response.data.data,
                company: resolveRelationship(response.data.data.relationships.company, response.data.included),
            };
        } catch (e) {
            unexpectedError(e);
        }

        return null;
    };

    const hasStatus = (status) => {
        if (!domain.value) {
            return false;
        }

        return domain.value.attributes.status === status;
    };

    return {
        loading,
        domain,
        registrantIdentity,
        billingIdentity,
        adminIdentity,
        techIdentity,
        tldRequirements,
        nameservers,
        subscription,
        status,
        canEditContacts,
        canLock,
        isLocked,
        isActive,
        isQuarantined,
        isCancelled,
        registrationFailed,
        transferFailed,
        isExpired,
        hasStatus,
        fetchDomain,
        refetchDomain,
    };
});
